import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Loader2, Clock, Zap, Info } from "lucide-react";

const TIMES_15 = (() => {
    const out = [];
    for (let h = 0; h < 24; h++) {
        for (const m of [0, 15, 30, 45]) {
            out.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
        }
    }
    return out;
})();

export default function NotificationDeliveryDialog({ open, onOpenChange, clientId, clientName }) {
    const [state, setState] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open || !clientId) return;
        setLoading(true);
        api.get(`/clients/${clientId}/notification-delivery`)
            .then(({ data }) => setState(data))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [open, clientId]);

    const patch = async (body, successMessage) => {
        setSaving(true);
        try {
            const { data } = await api.patch(`/clients/${clientId}/notification-delivery`, body);
            setState(data);
            toast.success(successMessage || "Delivery updated");
        } catch (err) {
            toast.error("Could not save", { description: formatApiErrorDetail(err.response?.data?.detail) });
        } finally {
            setSaving(false);
        }
    };

    const useWorkspaceDefault = state && state.mode === "immediate" && !state.digest_time;
    const isDigest = state?.mode === "daily_digest";

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[560px] rounded-xl border-slate-200" data-testid="delivery-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl tracking-tight inline-flex items-center gap-2">
                        <Clock className="w-4 h-4 text-[#C55A46]" /> Email delivery
                    </DialogTitle>
                    <DialogDescription className="text-slate-500">
                        Choose how <span className="text-slate-700 font-medium">{clientName}</span> receives portal updates.
                    </DialogDescription>
                </DialogHeader>

                {loading || !state ? (
                    <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
                ) : (
                    <div className="space-y-4">
                        <ModeCard
                            testid="delivery-mode-immediate"
                            icon={<Zap className="w-4 h-4 text-[#C55A46]" />}
                            title="Immediate"
                            desc="Each eligible event triggers its own email right away."
                            selected={state.mode === "immediate"}
                            disabled={saving}
                            onClick={() => patch({ mode: "immediate", digest_time: "" }, "Delivery set to Immediate")}
                        />
                        <ModeCard
                            testid="delivery-mode-daily-digest"
                            icon={<Clock className="w-4 h-4 text-[#C55A46]" />}
                            title="Daily Digest"
                            desc="Group non-urgent updates into one clean email each morning."
                            selected={state.mode === "daily_digest"}
                            disabled={saving}
                            onClick={() => patch({ mode: "daily_digest" }, "Delivery set to Daily Digest")}
                        />

                        {isDigest && (
                            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4" data-testid="delivery-digest-time-block">
                                <div className="flex items-center justify-between gap-4">
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium text-slate-900">Delivery time</p>
                                        <p className="text-xs text-slate-500 mt-1">
                                            Workspace default: <span className="text-slate-700 font-medium">{state.workspace_default_digest_time}</span> · <span className="text-slate-700 font-medium">{state.workspace_timezone}</span>
                                        </p>
                                    </div>
                                    <select
                                        data-testid="delivery-digest-time-select"
                                        disabled={saving}
                                        value={state.digest_time || ""}
                                        onChange={(e) => patch({ digest_time: e.target.value }, "Digest time updated")}
                                        className="rounded-md border border-slate-200 bg-white text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#C55A46]/30"
                                    >
                                        <option value="">Use workspace default</option>
                                        {TIMES_15.map((t) => (
                                            <option key={t} value={t}>{t}</option>
                                        ))}
                                    </select>
                                </div>
                                <p className="mt-3 text-xs text-slate-500 inline-flex items-center gap-1.5">
                                    <Info className="w-3.5 h-3.5" /> Effective delivery:{" "}
                                    <span className="font-medium text-slate-800">{state.effective_digest_time}</span>{" "}·{" "}
                                    <span className="font-medium text-slate-800">{state.workspace_timezone}</span>
                                    {state.digest_time && <span className="ml-1 px-1.5 py-0.5 rounded bg-white border border-slate-200 text-[10px] uppercase tracking-wider text-slate-500">Client override</span>}
                                </p>
                            </div>
                        )}

                        <div className="pt-1 text-xs text-slate-400">
                            Approval requests and other urgent alerts are always sent immediately, regardless of this setting.
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

function ModeCard({ icon, title, desc, selected, disabled, onClick, testid }) {
    return (
        <button
            type="button"
            data-testid={testid}
            aria-pressed={selected}
            disabled={disabled}
            onClick={onClick}
            className={`w-full text-left rounded-lg border p-4 transition ${selected ? "border-[#C55A46] bg-[#C55A46]/5 ring-1 ring-[#C55A46]/30" : "border-slate-200 hover:border-slate-300 bg-white"} ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
        >
            <div className="flex items-start gap-3">
                <div className="mt-0.5">{icon}</div>
                <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-900">{title}</p>
                    <p className="text-xs text-slate-500 mt-1 leading-relaxed">{desc}</p>
                </div>
            </div>
        </button>
    );
}
